/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Col,
  Divider,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import { toInteger } from 'utils/normalizers';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { predioToString } from 'utils/padronesToString';
import { gettersPadronesCajaMap } from 'utils/padrones';
import Select from 'components/Select';
import { padronesEnum } from '../enums';

const Fusion = ({
  disabled,
  prediosRelacionados = [],
  prediosTramite = [],
  cantidad: _cantidad,
  cantidadMaxima,
  predio,
}) => {
  const formInstance = Form.useFormInstance();
  const cantidad = Form.useWatch('cantidad', formInstance);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [predios, setPredios] = useState([]);

  useEffect(() => {
    if (!_cantidad) return;
    const fillCount = Math.max(0, _cantidad - prediosRelacionados.length);
    formInstance.setFieldsValue({
      cantidad: _cantidad,
      predios_relacionados: [
        ...prediosRelacionados.map((p) => p.id),
        ...(new Array(fillCount).fill(null)),
      ],
    });
    setPredios([
      ...prediosRelacionados,
      ...(new Array(fillCount).fill(null)),
    ]);
  }, [_cantidad, prediosRelacionados]);

  useEffect(() => {
    formInstance.setFieldValue('predios_relacionados_objects', predios);
  }, [predios]);

  const rules = {
    q: [
      { required: true },
    ],
    predio: [
      { required: true },
      {
        validator: async (_, val) => {
          const _predios = predios.filter((e) => e);
          if (!val || !_predios.length) return;

          const record = _predios.find((p) => p?.id === val);
          if (val === predio.id) throw new Error('No es necesario agregar el predio del trámite, ya está incluido');

          if (record?.json_predio || record.estados_globales === 2) {
            throw new Error('El predio ingresado se encuentra en proceso de trámite');
          }
          const predios_relacionados = formInstance.getFieldValue('predios_relacionados');
          if (predios_relacionados.filter((p) => p === val).length > 1) {
            throw new Error('Predio ha sido seleccionado más de una vez');
          }
          if (val < prediosRelacionados.length) {
            throw new Error(`Ya se han relacionado ${prediosRelacionados.length} predios, no es posible ingresar un número menor`);
          }
        },
      },
    ],
    cantidad: [
      { required: true },
      {
        validator: async (_, val) => {
          if (val && cantidadMaxima && val > cantidadMaxima) {
            throw new Error(`Ingrese un valor menor o igual a ${cantidadMaxima}`);
          }
        },
      },
    ],
  };

  return (
    <>
      <Form.Item name="predios_relacionados_objects" hidden>
        <Select />
      </Form.Item>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="Número de predios a fusionar"
          name="cantidad"
          rules={rules.cantidad}
          normalize={(v) => toInteger(v)}
        >
          <Input
            disabled={disabled}
            maxLength={cantidadMaxima ? cantidadMaxima.toString().length : undefined}
            onChange={(evt) => {
              let qty = toInteger(evt.target.value);
              if (!qty) return;
              if (qty >= 4294967295) {
                qty = 0;
              }
              if (qty < prediosRelacionados.length) {
                qty = prediosRelacionados.length;
              }
              // Preservar la información ya capturada (predios añadidos y
              // observaciones) de los índices que permanecen en la lista.
              const arr = new Array(qty)
                .fill(null)
                .map((_, i) => predios[i] ?? prediosRelacionados[i] ?? null);
              setPredios(arr);
              formInstance.setFieldValue('cantidad', qty);
              formInstance.setFieldValue('predios_relacionados', arr.map((i) => i?.id));
              formInstance.setFieldValue('predios_relacionados_objects', arr);
            }}
          />
        </Form.Item>
      </Col>
      {disabled ? (
        <Col span={24}>
          <Typography.Title level={4}>
            Predios
            {` (${_cantidad})`}
          </Typography.Title>
          <ul>
            {prediosRelacionados.map((p) => {
              const [clave, participes, direccion] = predioToString(p).split('-');
              return (
                <li key={p.id}>
                  <Typography.Text strong>
                    {clave}
                    {' - '}
                    {participes}
                  </Typography.Text>
                  <br />
                  {direccion}
                </li>
              );
            })}
          </ul>
        </Col>
      ) : new Array(cantidad || 0).fill(null).map((_, idx) => {
        const _disabled = prediosTramite.includes(predios[idx]?.id);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            <Col span={24}>
              <InputSearchPro
                rules={rules.q}
                advancedComponentProps={{ tipoDePadron: padronesEnum.PREDIO }}
                onClickAdvanced={() => setVisibleBusquedaPadrones(idx)}
                label={`Predio ${idx + 1}`}
                name={['predios_relacionados', `q${idx}`]}
                resultProps={{
                  dataSource: predios[idx],
                  setDataSource: (record) => {
                    if (record.id === predio.id) {
                      message.info('No es necesario agregar el predio del trámite, ya está incluido');
                      formInstance.resetFields([['predios_relacionados', idx]]);
                      return;
                    }
                    if (predios.some((e) => e?.id === record.id)) {
                      message.info('El predio ya se encuentra agregado');
                      formInstance.resetFields([['predios_relacionados', idx]]);
                      return;
                    }
                    if (record.json_predio || record.estados_globales === 2) {
                      message.info('El predio ingresado se encuentra en proceso de trámite');
                      formInstance.resetFields([['predios_relacionados', idx]]);
                      return;
                    }
                    const _predios = [...predios];
                    _predios[idx] = record;
                    setPredios(_predios);
                  },
                  name: ['predios_relacionados', idx],
                  label: `Predio ${idx + 1}`,
                  breakPoints: { md: 24 },
                  rules: rules.predio,
                  render: (item) => {
                    if (!item) return '';
                    return predioToString(item);
                  },
                  onClear: _disabled ? null : () => {
                    const _predios = [...predios];
                    _predios[idx] = null;
                    setPredios(_predios);
                    formInstance.resetFields([['predios_relacionados', idx]]);
                    formInstance.validateFields([['predios_relacionados', idx]]);
                  },
                  disabled: disabled || _disabled,
                }}
                breakPoints={{ md: 24 }}
                disabled={disabled || _disabled}
                getter={_disabled ? null : gettersPadronesCajaMap[padronesEnum.PREDIO]}
                qName="q"
              />
            </Col>
            <Col span={24}>
              <Form.Item label="Observaciones">
                <Input.TextArea
                  value={predios[idx]?.observaciones_fusion ?? ''}
                  onChange={({ target: { value } }) => {
                    setPredios((prev) => {
                      const next = [...prev];
                      if (next[idx]) {
                        next[idx] = { ...next[idx], observaciones_fusion: value };
                      }
                      return next;
                    });
                  }}
                  autoSize={{ minRows: 3 }}
                  disabled={!predios[idx] || _disabled}
                />
              </Form.Item>
            </Col>
            <Divider />
          </React.Fragment>
        );
      })}
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadrones !== false}
        setVisible={setVisibleBusquedaPadrones}
        tipoDePadron={padronesEnum.PREDIO}
        callback={(record) => {
          const _predios = [...predios];
          if (_predios.some((e) => e?.id === record.id)) {
            message.info('El predio ya se encuentra agregado');
            formInstance.resetFields([
              ['predios_relacionados', visibleBusquedaPadrones],
            ]);
            return;
          }
          if (record.id === predio.id) {
            message.info('No es necesario agregar el predio del trámite, ya está incluido');
            formInstance.resetFields([
              ['predios_relacionados', visibleBusquedaPadrones],
            ]);
            return;
          }
          if (record?.json_predio || record.estados_globales === 2) {
            message.info('El predio ingresado se encuentra en proceso de trámite');
            formInstance.resetFields([
              ['predios_relacionados', visibleBusquedaPadrones],
            ]);
            return;
          }
          _predios[visibleBusquedaPadrones] = record;
          setPredios(_predios);
          formInstance.setFieldValue(['predios_relacionados', visibleBusquedaPadrones], record.id);
        }}
      />
    </>
  );
};

export default Fusion;
